import styles from './ActivityIndicator.module.css';

export type ActivityMode = 'total' | 'meetings' | 'emails';

interface Props {
  emails7d: number;
  emails14d: number;
  emails30d: number;
  meetings7d: number;
  meetings14d: number;
  meetings30d: number;
  activityWeeks?: { week_start: string; emails: number; meetings: number }[];
  globalMax?: number;
  activityStale?: boolean;
  mode?: ActivityMode;
}

const BAR_HEIGHT = 32;
const COLORS = {
  emails: '#1565C0',
  meetings: '#E65100',
  total: '#2E7D32',
};

export function ActivityIndicator({
  emails7d,
  meetings7d,
  activityWeeks,
  globalMax,
  activityStale,
  mode = 'total',
}: Props) {
  const shimmerClass = activityStale ? ` ${styles.shimmer}` : '';
  const shimmerTitle = activityStale ? 'Activity refreshing…' : undefined;

  if (activityWeeks && activityWeeks.length > 0) {
    // globalMax is computed across deals using the largest absolute value.
    // For the timeline we scale each bar against the mode-appropriate max
    // so bars stay visually comparable across deals.
    const maxVal =
      globalMax ||
      Math.max(
        1,
        ...activityWeeks.map((w) =>
          mode === 'total' ? w.emails + w.meetings : mode === 'meetings' ? w.meetings : w.emails,
        ),
      );

    const tooltip = activityWeeks
      .map((w) => {
        const d = new Date(w.week_start + 'T00:00:00');
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${label}: ${w.meetings}/${w.emails}`;
      })
      .join('\n');

    return (
      <div className={styles.timeline + shimmerClass} title={shimmerTitle || tooltip}>
        {activityWeeks.map((w, i) => {
          const emailH = (w.emails / maxVal) * BAR_HEIGHT;
          const meetingH = (w.meetings / maxVal) * BAR_HEIGHT;
          const totalH = ((w.emails + w.meetings) / maxVal) * BAR_HEIGHT;
          const d = new Date(w.week_start + 'T00:00:00');
          const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          // Show label on first, middle, and last week
          const showLabel = i === 0 || i === activityWeeks.length - 1 || i === Math.floor(activityWeeks.length / 2);

          let bars;
          if (mode === 'total') {
            // Single stacked column: meetings at bottom, emails above (shared total bar)
            bars = (
              <>
                {w.meetings > 0 && (
                  <div
                    className={styles.bar}
                    style={{ height: Math.max(2, meetingH), background: COLORS.meetings }}
                  />
                )}
                {w.emails > 0 && (
                  <div
                    className={styles.bar}
                    style={{ height: Math.max(2, emailH), background: COLORS.emails }}
                  />
                )}
              </>
            );
            void totalH; // keep for future single-color variant
          } else if (mode === 'meetings') {
            bars = w.meetings > 0 && (
              <div
                className={styles.bar}
                style={{ height: Math.max(2, meetingH), background: COLORS.meetings }}
              />
            );
          } else {
            bars = w.emails > 0 && (
              <div
                className={styles.bar}
                style={{ height: Math.max(2, emailH), background: COLORS.emails }}
              />
            );
          }

          return (
            <div key={w.week_start} className={styles.weekCol}>
              <div className={styles.barStack} style={{ height: BAR_HEIGHT }}>
                {bars}
              </div>
              <span className={styles.weekLabel}>
                {showLabel ? label : ''}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Collapsed (no timeline): plain text, no icons.
  let text: string;
  if (mode === 'meetings') text = String(meetings7d);
  else if (mode === 'emails') text = String(emails7d);
  else text = `${meetings7d} / ${emails7d}`;
  const anyActivity = meetings7d > 0 || emails7d > 0;

  return (
    <div
      className={styles.root + shimmerClass}
      title={shimmerTitle || `${meetings7d} meetings / ${emails7d} emails (7d)`}
    >
      <span className={styles.metric} style={{ color: anyActivity ? '#444' : '#CCC' }}>
        {text}
      </span>
    </div>
  );
}
